import { useState, useEffect, useCallback } from 'react';
import { apiUtils } from '../services/api';

// Generic API hook
export const useApi = (apiFunction, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiFunction(...args);
      const result = apiUtils.formatResponse(response);
      
      setData(result.data);
      return result;
    } catch (err) {
      const errorResult = apiUtils.handleError(err);
      setError(errorResult.message);
      return errorResult;
    } finally {
      setLoading(false);
    }
  }, dependencies);

  return { data, loading, error, execute };
};

// Hook for automatic API calls
export const useApiCall = (apiFunction, dependencies = [], immediate = true) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);

  const execute = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiFunction(...args);
      const result = apiUtils.formatResponse(response);
      
      setData(result.data);
      return result;
    } catch (err) {
      const errorResult = apiUtils.handleError(err);
      setError(errorResult.message);
      return errorResult;
    } finally {
      setLoading(false);
    }
  }, dependencies);

  useEffect(() => {
    if (immediate) {
      execute();
    }
  }, [execute, immediate]);

  const refetch = useCallback(() => {
    return execute();
  }, [execute]);

  return { data, loading, error, execute, refetch };
};

// Hook for paginated data
export const usePaginatedApi = (apiFunction, initialParams = {}) => {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [params, setParams] = useState(initialParams);

  const fetchData = useCallback(async (newParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = { ...params, ...newParams };
      const response = await apiFunction(queryParams);
      const result = apiUtils.formatResponse(response);
      
      setData(result.data.data || result.data);
      setPagination(result.data.pagination || {
        page: 1,
        limit: 10,
        total: result.data.length || 0,
        pages: 1,
      });
      
      return result;
    } catch (err) {
      const errorResult = apiUtils.handleError(err);
      setError(errorResult.message);
      return errorResult;
    } finally {
      setLoading(false);
    }
  }, [apiFunction, params]);

  const loadMore = useCallback(() => {
    if (pagination.page < pagination.pages) {
      return fetchData({ page: pagination.page + 1 });
    }
  }, [fetchData, pagination]);

  const goToPage = useCallback((page) => {
    return fetchData({ page });
  }, [fetchData]);

  const updateParams = useCallback((newParams) => {
    setParams(prev => ({ ...prev, ...newParams }));
    return fetchData({ ...newParams, page: 1 });
  }, [fetchData]);

  const refresh = useCallback(() => {
    return fetchData({ page: 1 });
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    pagination,
    loading,
    error,
    loadMore,
    goToPage,
    updateParams,
    refresh,
  };
};

// Hook for form submissions
export const useFormSubmit = (apiFunction) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const submit = useCallback(async (formData) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      
      const response = await apiFunction(formData);
      const result = apiUtils.formatResponse(response);
      
      setSuccess(true);
      return result;
    } catch (err) {
      const errorResult = apiUtils.handleError(err);
      setError(errorResult.message);
      return errorResult;
    } finally {
      setLoading(false);
    }
  }, [apiFunction]);

  const reset = useCallback(() => {
    setError(null);
    setSuccess(false);
  }, []);

  return { submit, loading, error, success, reset };
};

// Hook for file uploads
export const useFileUpload = (uploadFunction) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const upload = useCallback(async (files, options = {}) => {
    try {
      setUploading(true);
      setError(null);
      setProgress(0);
      
      const response = await uploadFunction(files, {
        ...options,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentCompleted);
        },
      });
      
      const result = apiUtils.formatResponse(response);
      setUploadedFiles(prev => [...prev, ...result.data.files || [result.data]]);
      
      return result;
    } catch (err) {
      const errorResult = apiUtils.handleError(err);
      setError(errorResult.message);
      return errorResult;
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [uploadFunction]);

  const reset = useCallback(() => {
    setError(null);
    setUploadedFiles([]);
    setProgress(0);
  }, []);

  return { upload, uploading, progress, error, uploadedFiles, reset };
};

// Hook for search functionality
export const useSearch = (searchFunction, debounceMs = 300) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const debouncedSearch = useCallback(
    apiUtils.debounce(async (searchQuery) => {
      if (!searchQuery.trim()) {
        setResults([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const response = await searchFunction(searchQuery);
        const result = apiUtils.formatResponse(response);
        
        setResults(result.data);
      } catch (err) {
        const errorResult = apiUtils.handleError(err);
        setError(errorResult.message);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs),
    [searchFunction, debounceMs]
  );

  useEffect(() => {
    debouncedSearch(query);
  }, [query, debouncedSearch]);

  const search = useCallback((newQuery) => {
    setQuery(newQuery);
  }, []);

  const clear = useCallback(() => {
    setQuery('');
    setResults([]);
    setError(null);
  }, []);

  return { query, results, loading, error, search, clear };
};

// Hook for local storage state
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback((value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, storedValue]);

  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(initialValue);
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error);
    }
  }, [key, initialValue]);

  return [storedValue, setValue, removeValue];
};

// Hook for async operations with loading states
export const useAsync = (asyncFunction, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await asyncFunction(...args);
      setData(result);
      
      return result;
    } catch (err) {
      setError(err.message || 'An error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  }, dependencies);

  return { data, loading, error, execute };
};

// Hook for managing form state
export const useForm = (initialValues = {}, validationSchema = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setValue = useCallback((name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    setErrors(prev => {
      if (prev[name]) {
        return { ...prev, [name]: null };
      }
      return prev;
    });
  }, []);

  const setFieldTouched = useCallback((name, isTouched = true) => {
    setTouched(prev => ({ ...prev, [name]: isTouched }));
  }, []);

  const validate = useCallback(() => {
    const newErrors = {};
    
    Object.keys(validationSchema).forEach(field => {
      const rules = validationSchema[field];
      const value = values[field];
      
      if (rules.required && (!value || value.toString().trim() === '')) {
        newErrors[field] = `${field} is required`;
      } else if (rules.email && value && !apiUtils.isValidEmail(value)) {
        newErrors[field] = 'Invalid email format';
      } else if (rules.phone && value && !apiUtils.isValidPhone(value)) {
        newErrors[field] = 'Invalid phone number';
      } else if (rules.minLength && value && value.length < rules.minLength) {
        newErrors[field] = `Minimum ${rules.minLength} characters required`;
      } else if (rules.maxLength && value && value.length > rules.maxLength) {
        newErrors[field] = `Maximum ${rules.maxLength} characters allowed`;
      } else if (rules.pattern && value && !rules.pattern.test(value)) {
        newErrors[field] = rules.message || 'Invalid format';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [values, validationSchema]);

  const handleSubmit = useCallback(async (event, onSubmit) => {
    // Handle both event-based and direct calls
    if (typeof event === 'function') {
      onSubmit = event;
      event = null;
    }
    
    if (event) {
      event.preventDefault();
    }
    
    setIsSubmitting(true);
    
    // Mark all fields as touched
    const allTouched = Object.keys(values).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);
    
    // Validate inline to avoid dependency issues
    const newErrors = {};
    Object.keys(validationSchema).forEach(field => {
      const rules = validationSchema[field];
      const value = values[field];
      
      if (rules.required && (!value || value.toString().trim() === '')) {
        newErrors[field] = `${field} is required`;
      } else if (rules.email && value && !apiUtils.isValidEmail(value)) {
        newErrors[field] = 'Invalid email format';
      } else if (rules.phone && value && !apiUtils.isValidPhone(value)) {
        newErrors[field] = 'Invalid phone number';
      } else if (rules.minLength && value && value.length < rules.minLength) {
        newErrors[field] = `Minimum ${rules.minLength} characters required`;
      } else if (rules.maxLength && value && value.length > rules.maxLength) {
        newErrors[field] = `Maximum ${rules.maxLength} characters allowed`;
      } else if (rules.pattern && value && !rules.pattern.test(value)) {
        newErrors[field] = rules.message || 'Invalid format';
      }
    });
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      try {
        await onSubmit(values);
      } catch (error) {
        console.error('Form submission error:', error);
      }
    }
    
    setIsSubmitting(false);
  }, [values, validationSchema]);

  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  const isValid = Object.keys(errors).length === 0;
  const hasErrors = Object.keys(errors).some(key => errors[key]);

  return {
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    hasErrors,
    setValue,
    setFieldTouched,
    validate,
    handleSubmit,
    reset,
  };
};

export default useApi;
